'use client';

import { FC } from 'react';
import { Button, Typography } from '@mui/material';
import {
  SlideDownModal,
  CheckboxGroupMenu,
  CrossIcon,
  HouseKeyIcon,
  IconToggleGroup,
  MapIcon,
  Range,
  ResetIcon,
  WalletIcon,
} from '@/components';
import { useTranslations } from 'next-intl';
import { CATEGORY_ORDER, COLORS, MAX_FILTERS_RANGE_VALUES } from '@/constants';
import { getClampToMax } from '@/common';
import { Category, OfferType, OfferTypeObject, RangesDimensions } from '@/types';
import styled from 'styled-components';

const ModalHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ModalContent = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;
  width: 100%;
`;

const ModalControlBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const ControlButton = styled(Button)`
  && {
    width: 100%;
    height: 48px;
  }
`;

interface HeroMobileFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSearch: () => void;
  onSearchMap: () => void;
  offerTypeList: OfferTypeObject[];
  offerTypeValue: OfferType;
  setOfferTypeValue: (value: OfferTypeObject[]) => void;
  categoriesMenuList: Category[];
  categoriesValue: Category[];
  setCategoriesValue: (value: Category[]) => void;
  rangesDimensions: RangesDimensions;
  roomNumberValue: number[];
  setRoomNumberValue: (value: number[]) => void;
  priceValue: number[];
  setPriceValue: (value: number[]) => void;
  rentPriceValue: number[];
  setRentPriceValue: (value: number[]) => void;
  onReset: () => void;
  isPending?: boolean;
  isFiltersLoading?: boolean;
}

export const HeroMobileFiltersModal: FC<HeroMobileFiltersModalProps> = ({
  isOpen,
  onClose,
  onSearch,
  onSearchMap,
  offerTypeList,
  offerTypeValue,
  setOfferTypeValue,
  categoriesMenuList,
  categoriesValue,
  setCategoriesValue,
  rangesDimensions,
  roomNumberValue,
  setRoomNumberValue,
  priceValue,
  setPriceValue,
  rentPriceValue,
  setRentPriceValue,
  onReset,
  isPending,
  isFiltersLoading,
}) => {
  const t = useTranslations();

  const chosenOfferType = offerTypeList.filter((e) => e.code === offerTypeValue);

  const sellMaxValue = getClampToMax(
    rangesDimensions.price[1],
    MAX_FILTERS_RANGE_VALUES.SELL_PRICE,
  );
  const rentMaxValue = getClampToMax(
    rangesDimensions.rentPrice[1],
    MAX_FILTERS_RANGE_VALUES.RENT_PRICE,
  );

  return (
    <SlideDownModal isOpen={isOpen} onClose={onClose}>
      <ModalHead>
        <Typography fontSize={24} fontWeight={600} lineHeight="34px">
          {t('searchResult.filter')}
        </Typography>
        <Button variant="transparent" onClick={onClose} aria-label="Close">
          <CrossIcon aria-hidden="true" />
        </Button>
      </ModalHead>

      <ModalContent>
        <IconToggleGroup
          options={[
            {
              value: offerTypeList[0],
              title: offerTypeList[0]?.title,
              icon: <WalletIcon fill={COLORS.gray} />,
            },
            {
              value: offerTypeList[1],
              title: offerTypeList[1]?.title,
              icon: <HouseKeyIcon fill={COLORS.gray} />,
            },
          ]}
          multiple
          selected={chosenOfferType}
          onChange={setOfferTypeValue}
        />

        <CheckboxGroupMenu
          disabled={isPending || isFiltersLoading}
          label={t('searchResult.group')}
          items={categoriesMenuList}
          value={categoriesValue}
          isSelect
          hideCount
          onChange={setCategoriesValue}
          order={CATEGORY_ORDER}
          width="100%"
        />

        <Range
          disabled={isPending}
          label={t('searchResult.rooms')}
          minValue={rangesDimensions.roomNumber[0] || 0}
          maxValue={getClampToMax(rangesDimensions.roomNumber[1], MAX_FILTERS_RANGE_VALUES.ROOMS)}
          value={roomNumberValue}
          setValue={setRoomNumberValue}
          step={1}
          isAccordion={false}
        />

        {offerTypeValue === OfferType.SELL ? (
          <Range
            key="sell-price-range-modal"
            isPrice
            disabled={isPending}
            label={t('searchResult.purchasePrice')}
            minValue={rangesDimensions.price[0] || 0}
            maxValue={sellMaxValue}
            value={priceValue}
            setValue={setPriceValue}
            step={1}
            width="100%"
            isAccordion={false}
          />
        ) : (
          <Range
            key="rent-price-range-modal"
            isPrice
            disabled={isPending}
            label={t('searchResult.purchasePrice')}
            minValue={rangesDimensions.rentPrice[0] || 0}
            maxValue={rentMaxValue}
            value={rentPriceValue}
            setValue={setRentPriceValue}
            step={1}
            width="100%"
            isAccordion={false}
          />
        )}
      </ModalContent>

      <ModalControlBox>
        <ControlButton variant="primary" onClick={onSearch}>
          {t('mainHero.filter.button.submit')}
        </ControlButton>
        <ControlButton variant="outlined" startIcon={<MapIcon />} onClick={onSearchMap}>
          {t('mainHero.filter.button.map')}
        </ControlButton>
        <ControlButton variant="form-button" startIcon={<ResetIcon />} onClick={onReset}>
          {t('searchResult.clear')}
        </ControlButton>
      </ModalControlBox>
    </SlideDownModal>
  );
};
